/**
 * Browser Trades Tracker
 * Manages WebSocket subscription to trades feed for a specific asset
 * 
 * Key features:
 * - WebSocket subscription to trades data
 * - Maintains rolling buffer of 50 recent trades
 * - Emits CustomEvent for UI updates
 * - Singleton pattern to ensure only one instance per asset
 */

import { WebSocketTransport, SubscriptionClient } from '@nktkas/hyperliquid';

export class BrowserTradesTracker {
    constructor() {
        this.transport = null;
        this.subsClient = null;
        this.subscription = null;
        this.currentAsset = null;
        this.isConnected = false;
        this.isRunning = false;
        this.lastUpdate = null;
        this.trades = []; // Rolling buffer of recent trades
        this.maxTrades = 50;
    }

    /**
     * Start trades tracking for specified asset
     * @param {string} asset - Asset symbol (e.g., 'BTC', 'ETH')
     */
    async start(asset) {
        if (this.isRunning && this.currentAsset === asset) {
            // console.log(`[TRADES-TRACKER] Already running for ${asset}`);
            return;
        }

        // Stop existing subscription if switching assets
        if (this.isRunning && this.currentAsset !== asset) {
            try {
                await this.stop();
            } catch (error) {
                // Ignore errors from stop() - it's just cleanup
                // console.log('[TRADES-TRACKER] Ignoring stop error during asset switch:', error.message);
            }
        }

        try {
            // console.log(`[TRADES-TRACKER] Starting for ${asset}`);
            // Don't uppercase HIP-3 assets (xyz:NVDA must stay lowercase prefix)
            this.currentAsset = asset.includes(':') ? asset : asset.toUpperCase();
            const subscribedAsset = this.currentAsset;

            // Create WebSocket transport
            this.transport = new WebSocketTransport({
                url: 'wss://api.hyperliquid.xyz/ws',
                timeout: 10000,
                keepAliveInterval: 30000,
                resubscribe: true,
                reconnect: {
                    maxRetries: Infinity,
                    connectionTimeout: 10000,
                    reconnectionDelay: (attempt) => Math.min(1000 * Math.pow(1.5, attempt), 30000)
                }
            });

            // Create subscription client
            this.subsClient = new SubscriptionClient({ transport: this.transport });

            // Wait for connection
            await this.transport.ready();
            this.isConnected = true;
            // console.log(`[TRADES-TRACKER] Connected to WebSocket`);

            // Subscribe to trades
            await this.subscribeToTrades(subscribedAsset);

            this.isRunning = true;

        } catch (error) {
            // console.error(`[TRADES-TRACKER] Failed to start:`, error);
            throw error;
        }
    }

    /**
     * Subscribe to asset trades updates
     * @param {string} subscribedAsset - Asset to subscribe to
     */
    async subscribeToTrades(subscribedAsset) {
        try {
            this.subscription = await this.subsClient.trades(
                {
                    coin: subscribedAsset
                },
                (data) => this.handleTradesUpdate(data, subscribedAsset)
            );

            // console.log(`[TRADES-TRACKER] Subscribed to ${subscribedAsset} trades`);

        } catch (error) {
            // console.error('[TRADES-TRACKER] Failed to subscribe:', error);
            throw error;
        }
    }

    /**
     * Handle incoming trades updates
     * @param {array} trades - Array of trade data from WebSocket
     * @param {string} subscribedAsset - Asset this subscription is for
     */
    handleTradesUpdate(trades, subscribedAsset) {
        try {
            // Ignore updates if not running or no asset selected
            if (!this.isRunning || !this.currentAsset) {
                // console.log('[TRADES-TRACKER] IGNORING update - not running or no asset');
                return;
            }

            // Ignore if asset doesn't match
            if (subscribedAsset !== this.currentAsset) {
                return;
            }

            // Add new trades to front of array
            if (Array.isArray(trades)) {
                trades.forEach(trade => {
                    this.trades.unshift({
                        price: trade.px,
                        size: trade.sz,
                        side: trade.side, // 'A' = ask/sell, 'B' = bid/buy
                        time: trade.time,
                        timestamp: Date.now()
                    });
                });

                // Trim to max trades
                if (this.trades.length > this.maxTrades) {
                    this.trades = this.trades.slice(0, this.maxTrades);
                }
            }

            this.lastUpdate = new Date().toISOString();

            // console.log(`[TRADES-TRACKER] Broadcasting ${subscribedAsset} trades - total: ${this.trades.length}`);

            // Broadcast to UI
            const payload = {
                type: 'trades-data',
                asset: subscribedAsset,
                data: {
                    trades: this.trades,
                    timestamp: Date.now()
                },
                lastUpdate: this.lastUpdate,
                connected: this.isConnected
            };

            window.dispatchEvent(new CustomEvent('trades:external-data', {
                detail: payload
            }));

        } catch (error) {
            // console.error('[TRADES-TRACKER] Error broadcasting:', error);
        }
    }

    /**
     * Stop trades tracking
     */
    async stop() {
        try {
            // console.log(`[TRADES-TRACKER] Stopping for ${this.currentAsset}`);

            // Set flags first to prevent any pending callbacks from broadcasting
            this.isRunning = false;
            this.isConnected = false;
            const oldAsset = this.currentAsset;
            this.currentAsset = null;

            // Clear trades buffer
            this.trades = [];

            if (this.subscription) {
                // console.log(`[TRADES-TRACKER] Unsubscribing from ${oldAsset}`);
                await this.subscription.unsubscribe();
                this.subscription = null;
            }

            if (this.subsClient) {
                this.subsClient = null;
            }

            // Close the WebSocket transport to stop ALL data flow
            if (this.transport) {
                // console.log(`[TRADES-TRACKER] Closing WebSocket transport for ${oldAsset}`);
                try {
                    await this.transport.close();
                } catch (closeError) {
                    // Ignore TERMINATED_BY_USER and other close errors - connection is closing anyway
                    // console.log(`[TRADES-TRACKER] Ignoring transport close error:`, closeError.message);
                }
                this.transport = null;
            }

            // console.log(`[TRADES-TRACKER] Stopped ${oldAsset}`);

        } catch (error) {
            // console.error('[TRADES-TRACKER] Error stopping:', error);
            // Don't rethrow - stop() should always succeed
        }
    }

    /**
     * Change asset (stop current, start new)
     */
    async changeAsset(newAsset) {
        try {
            await this.stop();
        } catch (error) {
            // Ignore stop errors
            // console.log('[TRADES-TRACKER] Ignoring stop error during asset change:', error.message);
        }
        await this.start(newAsset);
    }
}

// Create singleton instance
if (typeof window !== 'undefined') {
    window.BrowserTradesTracker = new BrowserTradesTracker();
    // console.log('✅ BrowserTradesTracker initialized');
}

export default BrowserTradesTracker;
